import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

/* Advertising banners admin API.
   ─────────────────────────────────────────────────────────────────────────
   Every URL is relative and resolved against REACT_APP_BACKEND_HOST. CRA
   inlines that at BUILD time, so the staging bundle talks to dev-api and the
   production bundle talks to production — never hardcode a host here (see the
   STAGING PATCH note in blogServices.js for what happens when you do).

   Create and update send multipart/form-data because they carry the image
   file. fetchBaseQuery leaves a FormData body alone and, crucially, does NOT
   set Content-Type on it — the browser must add its own multipart boundary. */

export const bannerApi = createApi({
    reducerPath: 'bannerApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST}/api`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Banners', 'Banner'],
    endpoints: (build) => ({
        getBanners: build.query({
            query: (params = {}) => ({
                url: '/banners',
                params,
                method: 'GET',
            }),
            providesTags: ['Banners'],
            transformResponse: (response) => response?.data ?? [],
        }),
        getBannerById: build.query({
            query: (id) => ({
                url: `/banners/${id}`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'Banner', id }],
            transformResponse: (response) => response?.data ?? null,
        }),
        /* Per-banner click analytics for BannerReportModal. `days` is
           7/30/90 or "all"; the backend defaults to 30. */
        getBannerStats: build.query({
            query: ({ id, days = 30 }) => ({
                url: `/banners/${id}/stats`,
                params: { days },
                method: 'GET',
            }),
            providesTags: (result, error, { id }) => [{ type: 'Banner', id: `stats-${id}` }],
        }),
        /* Side-by-side stats for up to 8 banners (BannerCompareModal). */
        getBannerCompare: build.query({
            query: ({ ids = [], days = 30 }) => ({
                url: '/banners/compare',
                params: { ids: ids.join(','), days },
                method: 'GET',
            }),
        }),
        createBanner: build.mutation({
            query: (formData) => ({
                url: '/banners',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Banners'],
        }),
        updateBanner: build.mutation({
            query: ({ id, formData }) => ({
                url: `/banners/${id}`,
                method: 'PUT',
                body: formData,
            }),
            invalidatesTags: (result, error, { id }) => [
                'Banners',
                { type: 'Banner', id },
            ],
        }),
        deleteBanner: build.mutation({
            query: (id) => ({
                url: `/banners/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Banners'],
        }),
    }),
});

export const {
    useGetBannersQuery,
    useGetBannerByIdQuery,
    useGetBannerStatsQuery,
    useGetBannerCompareQuery,
    useCreateBannerMutation,
    useUpdateBannerMutation,
    useDeleteBannerMutation,
} = bannerApi;
